import React from 'react';
import { Link } from 'react-router-dom';

const SubscriptionRequired = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Hero Section */}
        <div className="relative overflow-hidden rounded-3xl shadow-2xl bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 p-8 md:p-12 mb-8">
          {/* Background decorative elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2"></div>
          <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-white/5 rounded-full"></div>
          
          <div className="relative z-10 text-center">
            <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-6">
              <span className="text-5xl">🔒</span>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Fonctionnalité réservée aux Auteurs
            </h1>
            
            <p className="text-white/90 text-lg md:text-xl max-w-xl mx-auto">
              Les abonnements et la boutique sont disponibles uniquement pour les comptes 
              <span className="font-bold text-yellow-300"> Auteur</span>.
            </p>
          </div>
        </div>

        {/* Explanation Card */}
        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 mb-6">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <span className="text-2xl">💡</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">Pourquoi cette restriction ?</h2>
              <p className="text-gray-600">
                Les abonnements permettent aux <strong>Auteurs</strong> de publier des projets, 
                créer des appels à candidatures et accéder à des fonctionnalités avancées de gestion. 
                En tant qu'utilisateur, vous pouvez postuler aux opportunités gratuitement !
              </p>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-6">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <span>✨</span> Ce que vous pouvez faire en tant qu'utilisateur simple :
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                <span className="text-green-500 text-xl">✓</span>
                <span className="text-gray-700">Postuler aux opportunités</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                <span className="text-green-500 text-xl">✓</span>
                <span className="text-gray-700">Créer votre profil professionnel</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                <span className="text-green-500 text-xl">✓</span>
                <span className="text-gray-700">Suivre vos candidatures</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                <span className="text-green-500 text-xl">✓</span>
                <span className="text-gray-700">Recevoir des notifications</span>
              </div>
            </div>
          </div>
        </div>

        {/* Become Author CTA */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl p-6 md:p-8 mb-6">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center flex-shrink-0">
              <span className="text-4xl">🚀</span>
            </div>
            <div className="flex-1 text-center md:text-left">
              <h2 className="text-xl font-bold text-gray-800 mb-2">
                Vous voulez créer vos propres projets ?
              </h2>
              <p className="text-gray-600 mb-4">
                Devenez <strong>Auteur</strong> et débloquez toutes les fonctionnalités : 
                création de projets, gestion d'équipe, abonnements, boutique et plus encore !
              </p>
              <Link
                to="/profile"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl hover:scale-105"
              >
                <span>✨</span>
                <span>Devenir Auteur</span>
                <span>→</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Benefits Preview */}
        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 mb-6">
          <h3 className="text-xl font-bold text-gray-800 mb-6 text-center">
            🎁 Avantages exclusifs pour les Auteurs
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-indigo-50 rounded-xl">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl">🎬</span>
              </div>
              <h4 className="font-semibold text-gray-800 mb-1">Créer des projets</h4>
              <p className="text-sm text-gray-600">Publiez vos projets et recrutez des talents</p>
            </div>
            
            <div className="text-center p-4 bg-purple-50 rounded-xl">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl">👥</span>
              </div>
              <h4 className="font-semibold text-gray-800 mb-1">Gérer des équipes</h4>
              <p className="text-sm text-gray-600">Constituez et gérez vos collaborateurs</p>
            </div>
            
            <div className="text-center p-4 bg-pink-50 rounded-xl">
              <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl">💰</span>
              </div>
              <h4 className="font-semibold text-gray-800 mb-1">Facturation intégrée</h4>
              <p className="text-sm text-gray-600">Gérez budgets et paiements facilement</p>
            </div>
          </div>
        </div>

        {/* Back Navigation */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/pour-toi"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-gray-700 font-medium rounded-xl border-2 border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 transition"
          >
            <span>🏠</span>
            <span>Voir les opportunités</span>
          </Link>
          
          <Link
            to="/my-applications"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-gray-700 font-medium rounded-xl border-2 border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 transition"
          >
            <span>📝</span>
            <span>Mes candidatures</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionRequired;
